import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserStore } from '@core/user/useUserStore';
import { useDaySequenceStore } from '@core/daySequence/useDaySequenceStore';
import { timesheetApprovalActions } from '@core/timesheetApproval/timesheetApproval.action';
import {
  type TimesheetApprovalItem,
  TimesheetApprovalView,
} from '@core/timesheetApproval/timesheetApproval.types';
import usePortalAccess from '@/access/usePortalAccess';
import { useTenantId } from '@/contexts/TenantContext';

const TIMESHEETS_PER_PAGE = 200;

type UseTimesheetApprovals = {
  items: TimesheetApprovalItem[];
  /** True until this tab's first ever load has finished. */
  isLoading: boolean;
  /** True while this tab's already-loaded items are refreshed in the background. */
  isRefreshing: boolean;
  /** True while later pages load in the background. */
  isFetchingMore: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  /** Approves the given day sequences and returns how many were approved. */
  approve: (daySequenceIds: string[]) => Promise<number>;
};

type ItemsByView = Record<TimesheetApprovalView, TimesheetApprovalItem[]>;

const EMPTY_ITEMS_BY_VIEW: ItemsByView = {
  [TimesheetApprovalView.Pending]: [],
  [TimesheetApprovalView.Approved]: [],
};

const withView = (
  views: Set<TimesheetApprovalView>,
  view: TimesheetApprovalView,
) => new Set(views).add(view);

const withoutView = (
  views: Set<TimesheetApprovalView>,
  view: TimesheetApprovalView,
) => {
  const next = new Set(views);
  next.delete(view);
  return next;
};

const mergeItems = (
  current: TimesheetApprovalItem[],
  incoming: TimesheetApprovalItem[],
  approvedIds: Set<string>,
) => {
  const seen = new Set(current.map((item) => item.daySequence.id));
  const added = incoming.filter(
    (item) =>
      !seen.has(item.daySequence.id) && !approvedIds.has(item.daySequence.id),
  );
  return added.length > 0 ? [...current, ...added] : current;
};

/**
 * Loads the timesheets for the Timesheets page. Tenant users see every client
 * in the tenancy; client users only see their own client.
 *
 * Requires approval and Approved each load once per visit to the page.
 * Switching back shows that list and does not fetch again. A page reload,
 * a changed tenant or client, or an explicit refetch loads a fresh copy.
 * Later pages of a first load still arrive in the background.
 */
export const useTimesheetApprovals = (
  view: TimesheetApprovalView | null,
): UseTimesheetApprovals => {
  const tenantId = useTenantId();
  const access = usePortalAccess();
  const currentUserClientId = useUserStore(
    (state) => state.currentUser?.clientId,
  );
  const approveDaySequences = useDaySequenceStore(
    (state) => state.approveDaySequences,
  );
  const clientId = access.isClientPortal
    ? (currentUserClientId ?? undefined)
    : undefined;
  const canFetch = !!tenantId && (!access.isClientPortal || !!clientId);

  const [itemsByView, setItemsByView] = useState<ItemsByView>(
    EMPTY_ITEMS_BY_VIEW,
  );
  const [loadedViews, setLoadedViews] = useState<Set<TimesheetApprovalView>>(
    () => new Set(),
  );
  const [fetchingViews, setFetchingViews] = useState<
    Set<TimesheetApprovalView>
  >(() => new Set());
  const [fetchingMoreViews, setFetchingMoreViews] = useState<
    Set<TimesheetApprovalView>
  >(() => new Set());
  const [error, setError] = useState<string | null>(null);
  // Per tab, bumped on every new load and scope change, so an outdated
  // request for that tab is dropped when it resolves. Kept per tab so loading
  // one tab never cancels another.
  const loadSessionByViewRef = useRef<Record<TimesheetApprovalView, number>>({
    [TimesheetApprovalView.Pending]: 0,
    [TimesheetApprovalView.Approved]: 0,
  });
  // Approved during this session, so a page already in flight cannot re-add
  // an item to the Pending cache.
  const approvedIdsRef = useRef<Set<string>>(new Set());

  const prefetchRemainingPages = useCallback(
    async (
      fetchView: TimesheetApprovalView,
      initialLastKey: string,
      loadSession: number,
    ) => {
      if (!tenantId) return;

      const isCurrentLoad = () =>
        loadSession === loadSessionByViewRef.current[fetchView];
      let nextKey: string | null = initialLastKey;
      setFetchingMoreViews((current) => withView(current, fetchView));
      try {
        while (nextKey && isCurrentLoad()) {
          const page = await timesheetApprovalActions.getTimesheetsForApproval(
            tenantId,
            fetchView,
            clientId,
            TIMESHEETS_PER_PAGE,
            nextKey,
          );

          if (!isCurrentLoad()) return;

          setItemsByView((current) => ({
            ...current,
            [fetchView]: mergeItems(
              current[fetchView],
              page.items,
              approvedIdsRef.current,
            ),
          }));
          nextKey = page.hasMore ? (page.lastEvaluatedKey ?? null) : null;
        }
      } catch (fetchError) {
        if (!isCurrentLoad()) return;
        console.error('Error fetching more timesheets:', fetchError);
        setError('Unable to load all timesheets');
      } finally {
        if (isCurrentLoad()) {
          setFetchingMoreViews((current) => withoutView(current, fetchView));
        }
      }
    },
    [tenantId, clientId],
  );

  const load = useCallback(
    async (fetchView: TimesheetApprovalView) => {
      if (!canFetch || !tenantId) return;

      loadSessionByViewRef.current[fetchView] += 1;
      const loadSession = loadSessionByViewRef.current[fetchView];
      const isCurrentLoad = () =>
        loadSession === loadSessionByViewRef.current[fetchView];

      setFetchingViews((current) => withView(current, fetchView));
      setError(null);
      try {
        const page = await timesheetApprovalActions.getTimesheetsForApproval(
          tenantId,
          fetchView,
          clientId,
          TIMESHEETS_PER_PAGE,
        );

        if (!isCurrentLoad()) return;

        setItemsByView((current) => ({ ...current, [fetchView]: page.items }));
        setLoadedViews((current) => new Set(current).add(fetchView));
        if (page.hasMore && page.lastEvaluatedKey) {
          void prefetchRemainingPages(
            fetchView,
            page.lastEvaluatedKey,
            loadSession,
          );
        }
      } catch (fetchError) {
        if (!isCurrentLoad()) return;
        console.error('Error fetching timesheets for approval:', fetchError);
        setError('Unable to load timesheets');
      } finally {
        if (isCurrentLoad()) {
          setFetchingViews((current) => withoutView(current, fetchView));
        }
      }
    },
    [canFetch, tenantId, clientId, prefetchRemainingPages],
  );

  const refetch = useCallback(async () => {
    if (!view) return;
    await load(view);
  }, [view, load]);

  const startedViewsRef = useRef(new Set<TimesheetApprovalView>());

  // A different tenant or client scope invalidates every cached tab. This
  // runs before the load effect so the current tab fetches again afterwards.
  const scopeKey = `${tenantId ?? ''}:${clientId ?? ''}`;
  const scopeKeyRef = useRef(scopeKey);
  useEffect(() => {
    if (scopeKeyRef.current === scopeKey) return;
    scopeKeyRef.current = scopeKey;
    loadSessionByViewRef.current[TimesheetApprovalView.Pending] += 1;
    loadSessionByViewRef.current[TimesheetApprovalView.Approved] += 1;
    startedViewsRef.current = new Set();
    setItemsByView(EMPTY_ITEMS_BY_VIEW);
    setLoadedViews(new Set());
    setFetchingViews(new Set());
    setFetchingMoreViews(new Set());
  }, [scopeKey]);

  useEffect(() => {
    if (!view || !canFetch || startedViewsRef.current.has(view)) return;
    startedViewsRef.current.add(view);
    void load(view);
  }, [view, canFetch, load]);

  const approve = useCallback(
    async (daySequenceIds: string[]) => {
      if (daySequenceIds.length === 0) return 0;
      const approved = await approveDaySequences(daySequenceIds);
      const approvedIds = new Set(
        approved.map((daySequence) => daySequence.id),
      );
      if (approvedIds.size > 0) {
        approvedIds.forEach((id) => approvedIdsRef.current.add(id));
        setItemsByView((current) => ({
          ...current,
          [TimesheetApprovalView.Pending]: current[
            TimesheetApprovalView.Pending
          ].filter((item) => !approvedIds.has(item.daySequence.id)),
        }));
      }
      return approvedIds.size;
    },
    [approveDaySequences],
  );

  const items = view ? itemsByView[view] : [];
  const hasLoadedView = view ? loadedViews.has(view) : false;
  const isFetchingThisView = view ? fetchingViews.has(view) : false;

  return {
    items,
    isLoading: isFetchingThisView && !hasLoadedView,
    isRefreshing: isFetchingThisView && hasLoadedView,
    isFetchingMore: view ? fetchingMoreViews.has(view) : false,
    error,
    refetch,
    approve,
  };
};
